/** Docked live preview for the selected delivery method: re-renders it through
 *  /notification_test_render/ ~600ms after the last edit, applying the draft's
 *  unsaved field overrides (shared buildOverrides seam) so the preview always
 *  shows what the editor holds, not what is saved.
 *
 *  The pane is a resizable (drag the left edge), sticky flex sibling of the
 *  editor body — same in-flow idiom as ContextSchemaDrawer on lg+. The render
 *  context (pasted JSON / delivery-log id / entity id) is the page-lifted
 *  sticky source shared with TestRenderDialog, so picking a booking once
 *  serves both surfaces.
 */
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { ConsoleApiError } from '@/api/client';
import type { NotificationDeliveryMethod, NotificationDetail } from '@/api/notifications';
import {
  listTestRenderEvents,
  testRender,
  type TestRenderRequest,
  type TestRenderResult,
} from '@/api/notificationTestRender';

import {
  buildEvaluateContext,
  contextSourceComplete,
  ContextSourcePicker,
  type ContextSourceValue,
} from '../test-render/ContextSourcePicker';
import { buildOverrides } from '../test-render/draftOverrides';
import { RenderedBodyView } from '../test-render/RenderedBodyView';
import type { NotificationDraft } from '../useNotificationDraft';
import { channelSpec } from './channelFields';

const WIDTH_KEY = 'nf-live-preview-width';
const HEIGHT_KEY = 'nf-live-preview-height';
const MIN_WIDTH = 340;
const MAX_WIDTH = 960;
const DEFAULT_WIDTH = 440;
const MIN_HEIGHT = 240;
const DEFAULT_HEIGHT = 520;
const RENDER_DEBOUNCE_MS = 600;

type PreviewWidth = 320 | 600 | 'full';

function initialPaneWidth(): number {
  const raw = Number(localStorage.getItem(WIDTH_KEY));
  return Number.isFinite(raw) && raw >= MIN_WIDTH && raw <= MAX_WIDTH ? raw : DEFAULT_WIDTH;
}

function initialPreviewHeight(): number {
  const raw = Number(localStorage.getItem(HEIGHT_KEY));
  return Number.isFinite(raw) && raw >= MIN_HEIGHT ? raw : DEFAULT_HEIGHT;
}

interface Props {
  detail: NotificationDetail;
  draft: NotificationDraft;
  dm: NotificationDeliveryMethod;
  /** Page-lifted sticky render context (shared with TestRenderDialog). */
  source: ContextSourceValue;
  onSourceChange: (v: ContextSourceValue) => void;
  onClose: () => void;
}

export function LivePreviewPane({ detail, draft, dm, source, onSourceChange, onClose }: Props) {
  const isNew = dm.id < 0;
  const isEmail = channelSpec(dm.method).channel === 'email';

  // ---- pane resize (persisted across sessions) -----------------------------
  const [paneWidth, setPaneWidth] = useState<number>(initialPaneWidth);
  const widthRef = useRef(paneWidth);
  widthRef.current = paneWidth;
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const startDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startWidth: widthRef.current };
  };
  const moveDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const s = dragRef.current;
    if (!s) return;
    setPaneWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, s.startWidth + (s.startX - e.clientX))));
  };
  const endDrag = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try {
      localStorage.setItem(WIDTH_KEY, String(widthRef.current));
    } catch {
      /* best effort */
    }
  };

  // ---- view toggles ---------------------------------------------------------
  const [previewWidth, setPreviewWidth] = useState<PreviewWidth>('full');
  const [dark, setDark] = useState(false);
  const [debugMarkers, setDebugMarkers] = useState(false);
  const [view, setView] = useState<'preview' | 'plain'>('preview');
  // Open the context section until a usable context has been picked.
  const [contextOpen, setContextOpen] = useState(
    () => source.mode === 'json' && source.jsonText.trim() === '',
  );

  // ---- render context -------------------------------------------------------
  const { data: events = [] } = useQuery({
    queryKey: ['notification-test-render-events'],
    queryFn: listTestRenderEvents,
    staleTime: 5 * 60_000,
  });
  const eventInfo = events.find((e) => e.code === detail.notification.event);
  const entityKind = eventInfo?.entity_kind ?? null;

  // ---- debounced draft-aware render ----------------------------------------
  const { fieldOverrides, notificationOverrides } = useMemo(
    () => buildOverrides(draft, dm.id),
    // dirtyPaths is replaced (never mutated) on every draft edit.
    [draft, dm.id],
  );
  const hasOverrides = fieldOverrides !== null || notificationOverrides !== null;

  const canRender = !isNew && contextSourceComplete(source, entityKind);
  const requestBody = useMemo<TestRenderRequest | null>(() => {
    if (!canRender) return null;
    return {
      notification: detail.id,
      delivery_method: dm.id,
      context: buildEvaluateContext(source),
      debug_markers: debugMarkers,
      ...(fieldOverrides ? { field_overrides: fieldOverrides } : {}),
      ...(notificationOverrides ? { notification_overrides: notificationOverrides } : {}),
    };
  }, [canRender, detail.id, dm.id, source, debugMarkers, fieldOverrides, notificationOverrides]);
  const requestKey = useMemo(
    () => (requestBody ? JSON.stringify(requestBody) : null),
    [requestBody],
  );
  const bodyRef = useRef(requestBody);
  bodyRef.current = requestBody;

  // Keep the last good render on screen while the next one is in flight —
  // useMutation resets `data` on every mutate, which would blank the pane on
  // each keystroke.
  const [result, setResult] = useState<TestRenderResult | null>(null);
  const mutation = useMutation({ mutationFn: testRender, onSuccess: setResult });
  const { mutate } = mutation;

  useEffect(() => setResult(null), [dm.id]);

  useEffect(() => {
    if (!requestKey) return undefined;
    const t = setTimeout(() => {
      if (bodyRef.current) mutate(bodyRef.current);
    }, RENDER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [requestKey, mutate]);

  const requestError =
    mutation.error instanceof ConsoleApiError
      ? mutation.error.message
      : mutation.error
        ? 'Test render request failed.'
        : null;

  const res =
    result?.results.find((r) => r.delivery_method === dm.id) ?? result?.results[0] ?? null;

  return (
    <Paper
      variant="outlined"
      sx={{
        width: paneWidth,
        flexShrink: 0,
        alignSelf: 'flex-start',
        position: 'sticky',
        top: 0,
        maxHeight: 'calc(100vh - 120px)',
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the live preview"
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        sx={{
          width: 10,
          flexShrink: 0,
          cursor: 'col-resize',
          touchAction: 'none',
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // The grip: without a visible affordance nobody discovers the
          // pane is resizable.
          '&::after': {
            content: '""',
            width: 3,
            height: 44,
            borderRadius: 2,
            bgcolor: 'text.disabled',
          },
          '&:hover': { bgcolor: 'action.selected' },
        }}
      />
      <Stack spacing={1} sx={{ flex: 1, minWidth: 0, p: 1.5, overflow: 'auto' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            Live preview
          </Typography>
          {hasOverrides && (
            <Chip size="small" color="warning" variant="outlined" label="unsaved draft" />
          )}
          <Tooltip title="Re-render now">
            <span>
              <IconButton
                size="small"
                aria-label="Re-render now"
                disabled={!requestBody || mutation.isPending}
                onClick={() => bodyRef.current && mutate(bodyRef.current)}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" aria-label="Close the live preview" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Accordion
          variant="outlined"
          disableGutters
          expanded={contextOpen}
          onChange={(_, x) => setContextOpen(x)}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Render context</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              <ContextSourcePicker
                value={source}
                onChange={onSourceChange}
                entityKind={entityKind}
                entityFamily={eventInfo?.family}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {isEmail && (
            <>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={previewWidth}
                onChange={(_, v: PreviewWidth | null) => v && setPreviewWidth(v)}
                aria-label="Preview width"
              >
                <ToggleButton value={320}>320</ToggleButton>
                <ToggleButton value={600}>600</ToggleButton>
                <ToggleButton value="full">Full</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={view}
                onChange={(_, v: 'preview' | 'plain' | null) => v && setView(v)}
                aria-label="Preview mode"
              >
                <ToggleButton value="preview">Preview</ToggleButton>
                <ToggleButton value="plain">Plain</ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title="Simulate a dark-mode email client (inverts the preview)">
                <ToggleButton
                  size="small"
                  value="dark"
                  selected={dark}
                  onChange={() => setDark((d) => !d)}
                  aria-label="Simulate dark mode"
                >
                  <DarkModeOutlinedIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="Block boundary markers — wrap each block value in <!-- block:… --> comments">
            <ToggleButton
              size="small"
              value="markers"
              selected={debugMarkers}
              onChange={() => setDebugMarkers((d) => !d)}
              aria-label="Block boundary markers"
            >
              <CodeIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </Stack>

        <Box sx={{ height: 4 }}>{mutation.isPending && <LinearProgress />}</Box>

        {isNew ? (
          <Alert severity="info">
            Save the notification first — the live preview needs a persisted delivery method.
          </Alert>
        ) : !canRender ? (
          <Alert severity="info">Pick a render context above to start the live preview.</Alert>
        ) : (
          <>
            {requestError && <Alert severity="error">{requestError}</Alert>}
            {result && !result.resolved && (
              <Alert severity="warning" sx={{ py: 0 }}>
                Some lazy context fields could not be resolved — output may be incomplete.
              </Alert>
            )}
            {res?.error && <Alert severity="error">{res.error}</Alert>}
            {res?.subject != null && (
              <Typography variant="body2">
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  Subject:{' '}
                </Box>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {res.subject}
                </Box>
              </Typography>
            )}
            {res?.body != null && (
              // Vertically resizable via the native bottom-edge grip; the
              // chosen height persists across sessions.
              <Box
                sx={{
                  resize: 'vertical',
                  overflow: 'hidden',
                  height: initialPreviewHeight(),
                  minHeight: MIN_HEIGHT,
                  '& > div': { height: '100%' },
                  '& iframe': { height: '100%' },
                }}
                ref={(el: HTMLDivElement | null) => {
                  if (!el) return;
                  new ResizeObserver(() => {
                    try {
                      localStorage.setItem(HEIGHT_KEY, String(Math.round(el.offsetHeight)));
                    } catch {
                      /* best effort */
                    }
                  }).observe(el);
                }}
              >
                <RenderedBodyView
                  body={res.body}
                  preview={isEmail && view === 'preview'}
                  title={`Live preview (delivery method ${res.delivery_method})`}
                  width={previewWidth}
                  dark={dark}
                  height="100%"
                />
              </Box>
            )}
            {result && res == null && (
              <Typography variant="body2" color="text.secondary">
                The render returned no output for this delivery method.
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}
